import React, { useState } from 'react';
import { TrendingDown, TrendingUp, Minus, ArrowRight, ChevronDown } from 'lucide-react';

/**
 * SkinCheckInCard — the compact TRACKER module shown for every repeat face photo.
 *
 * Design (Sofia, 2026-08): answering and tracking are two different jobs.
 *   · If the user asked something, the ANSWER + reasoning render above as normal
 *     chat prose. This module then sits underneath as the tracker.
 *   · If they just sent a photo with no question, this module is the whole reply.
 * It carries NO narrative of its own: the delta rows are the week-to-week signal
 * and the running summary belongs in the Skin Journal. When something
 * profile-level moves (undertone / skin type) we state the change itself and
 * stop there. That's why it can never duplicate the prose above it.
 *
 * Deliberately lighter than a result card: no shadow, tighter type, a quiet link
 * into the Skin Journal rather than a heavy CTA. It's secondary by design.
 */

const DIRECTION = {
  improving: { Icon: TrendingDown, cls: 'text-[#047857] bg-[#ECFDF5] ring-1 ring-[#A7F3D0]' },
  steady:    { Icon: Minus,        cls: 'text-[#2A324B]/60 bg-[#F0F0F0]' },
  flaring:   { Icon: TrendingUp,   cls: 'text-[#B45309] bg-[#FFFBEB] ring-1 ring-[#FDE68A]' },
};

const FIELD_LABEL = { undertone: 'Undertone', skin_type: 'Skin type', depth: 'Depth' };

function ChangeRow({ change }) {
  const [open, setOpen] = useState(false);
  const dir = DIRECTION[change.direction] || DIRECTION.steady;
  const { Icon } = dir;
  const canOpen = !!change.evidence;

  return (
    <div className="border-b border-[#341C1C]/[0.06] last:border-b-0">
      <button
        type="button"
        onClick={() => canOpen && setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-3 py-2 text-left"
        aria-expanded={canOpen ? open : undefined}
      >
        <span className="flex items-center gap-1.5 min-w-0">
          <span className="text-sm text-[#2A324B] truncate">
            {change.isNew ? 'New: ' : ''}{change.concern}
          </span>
          {canOpen && (
            <ChevronDown
              className={`w-3 h-3 flex-none text-[#2A324B]/35 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
            />
          )}
        </span>
        <span className={`inline-flex items-center gap-1 flex-none rounded-full px-2 py-0.5 text-xs font-semibold ${dir.cls}`}>
          <Icon className="w-2.5 h-2.5" strokeWidth={2.4} />
          {change.word}
        </span>
      </button>

      {canOpen && (
        <div className={`grid transition-[grid-template-rows] duration-200 ${open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
          <div className="overflow-hidden">
            <p className="text-[13px] leading-relaxed text-[#2A324B]/55 pb-2.5 pr-6">{change.evidence}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default function SkinCheckInCard({ checkin = {}, onOpenJournal, onConfirmUpdate }) {
  const {
    sinceLabel = 'your last scan',
    comparability = { ok: true },
    changes = [],
    profileUpdates = [],
    steady = false,
  } = checkin;

  return (
    <div className="rounded-xl border border-[#341C1C]/10 bg-[#FCFAF8] px-3.5 py-3">

      {/* one-line header — this is a module, not a result card */}
      <div className="flex items-baseline justify-between gap-2 mb-1.5">
        <span className="text-[12px] font-bold uppercase tracking-[0.14em] text-[#C8956A]">
          Skin check-in
        </span>
        <span className="text-[12px] text-[#2A324B]/45 truncate">
          vs {sinceLabel}
        </span>
      </div>

      {steady ? (
        <p className="text-sm leading-relaxed text-[#2A324B]/70 py-1">
          Nothing notable since your scan earlier today. Skin shifts over days, not hours, so I&rsquo;ll track what moves on your next check-in.
        </p>
      ) : comparability.ok ? (
        <div>
          {changes.map((c, i) => <ChangeRow key={i} change={c} />)}
        </div>
      ) : (
        <p className="text-sm leading-relaxed text-[#2A324B]/70 py-1">
          Hard to compare today{comparability.reason ? `. ${String(comparability.reason).replace(/[.,;:\s]+$/, '')}` : ''}. I won&rsquo;t guess at changes, so match your usual light and angle next time.
        </p>
      )}

      {/* No running narrative here at all — the deltas carry the week-to-week
          signal and the summary belongs in the Skin Journal. When something
          profile-level moves we state the CHANGE itself, nothing more. */}
      {profileUpdates.length > 0 && (
        <div className="mt-2.5 pt-2.5 border-t border-[#341C1C]/[0.06]">
          {profileUpdates.map((u, i) => (
            <div key={i} className="mb-1.5 last:mb-0">
              <p className="text-[13px] leading-snug text-[#2A324B]">
                <span className="font-semibold">{FIELD_LABEL[u.field] || u.field}</span> now reads{' '}
                <span className="font-semibold">{u.to}</span>
                <span className="text-[#2A324B]/50"> (was {u.from})</span>
              </p>
              {u.why && <p className="text-[13px] text-[#2A324B]/50 mt-0.5">{u.why}</p>}
              <button
                type="button"
                onClick={() => onConfirmUpdate && onConfirmUpdate(u)}
                className="text-xs font-medium text-[#C8956A] hover:text-[#A87A52] mt-0.5"
              >
                Doesn&rsquo;t sound right?
              </button>
            </div>
          ))}
        </div>
      )}

      {/* quiet invite into the Journal — secondary, not a primary CTA */}
      <button
        type="button"
        onClick={onOpenJournal}
        className="flex items-center gap-1 text-[13px] font-semibold text-[#2A324B] hover:text-[#2A324B]/70 mt-2.5 pt-2.5 border-t border-[#341C1C]/[0.06] w-full"
      >
        See your full journey
        <ArrowRight className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
